// ABOUTME: Settings modal component for configuring app preferences and UI visibility
// ABOUTME: Manages toggles for storage controls, memory usage display, and other app settings

import React from 'react';
import Modal from './Modal';
import { AppSettings } from '../types';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    settings: AppSettings;
    onSettingsChange: (settings: AppSettings) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ 
    isOpen, 
    onClose, 
    settings, 
    onSettingsChange 
}) => {
    const handleToggle = (key: keyof AppSettings) => {
        const newSettings = {
            ...settings,
            [key]: !settings[key]
        };
        onSettingsChange(newSettings);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Settings" maxWidth="max-w-md">
            <div className="space-y-6">
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white">Display Options</h3>
                    
                    {/* Show Storage Controls Toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="text-sm font-medium text-slate-300">
                                Show Storage Controls
                            </label>
                            <p className="text-xs text-slate-400 mt-1">
                                Display storage provider selection and seeding options
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleToggle('showStorageControls')}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                                settings.showStorageControls 
                                    ? 'bg-blue-600' 
                                    : 'bg-slate-600'
                            }`}
                            style={{ WebkitAppearance: 'none' }}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                                    settings.showStorageControls 
                                        ? 'translate-x-6' 
                                        : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Show Debug Overlay Toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="text-sm font-medium text-slate-300">
                                Show Debug Overlay
                            </label>
                            <p className="text-xs text-slate-400 mt-1">
                                Display technical details about storage mechanisms and Web Worker status
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleToggle('showDebugOverlay')}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                                settings.showDebugOverlay 
                                    ? 'bg-blue-600' 
                                    : 'bg-slate-600'
                            }`}
                            style={{ WebkitAppearance: 'none' }}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                                    settings.showDebugOverlay 
                                        ? 'translate-x-6' 
                                        : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>
                </div>

                {/* Info Section */}
                <div className="p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                    <h4 className="text-sm font-medium text-slate-300 mb-2">About Settings</h4>
                    <p className="text-xs text-slate-400">
                        Settings are automatically saved to your profile and will persist across sessions.
                        Changes take effect immediately.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end pt-4 border-t border-slate-700">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800"
                    >
                        Done
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SettingsModal;